import React, { useState } from 'react'
import { ButtonBase, Checkbox, Divider, ListItemText, ListSubheader, Menu, MenuItem, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import { advancedColors, surfaces } from '../../theme'
import { labelForCategory, styleForCategory } from './feedCategory'
import { AdvancedTrackDefinition } from '../../api/types'
import { TOUR_ANCHORS } from '../../tour/anchors'

type Props = {
  messagesOnly: boolean
  onToggleMessagesOnly: () => void
  options: string[]
  added: ReadonlySet<string>
  onToggle: (category: string) => void
  tracks?: AdvancedTrackDefinition[]
}

const CheckboxItem = ({
  label,
  checked,
  color,
  onClick,
}: {
  label: string
  checked: boolean
  color: string
  onClick: () => void
}) => (
  <MenuItem dense role="menuitemcheckbox" aria-checked={checked} onClick={onClick}>
    <Checkbox
      size="small"
      checked={checked}
      tabIndex={-1}
      inputProps={{ 'aria-hidden': true }}
      sx={{ p: 0.5, mr: 1, color, '&.Mui-checked': { color } }}
    />
    <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
  </MenuItem>
)

const FeedFilterMenu = ({ messagesOnly, onToggleMessagesOnly, options, added, onToggle, tracks = [] }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const count = options.filter((category) => added.has(category)).length + (messagesOnly ? 1 : 0)
  const active = count > 0
  const tone = surfaces.green

  return (
    <>
      <ButtonBase
        data-tour={TOUR_ANCHORS.feedFilter}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        sx={{
          height: 28,
          pl: 1,
          pr: 0.75,
          gap: 0.5,
          borderRadius: 14,
          border: `1px solid ${active ? tone.fg : advancedColors.line}`,
          backgroundColor: active ? tone.bg : advancedColors.surface,
          color: active ? tone.fg : advancedColors.inkMuted,
          '&:hover': { backgroundColor: active ? tone.bg : advancedColors.cardBg, borderColor: tone.fg },
          '&.Mui-focusVisible': { outline: `2px solid ${tone.fg}`, outlineOffset: 2 },
        }}
      >
        <FilterListRoundedIcon sx={{ fontSize: '1rem' }} />
        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: active ? 600 : 500, lineHeight: 1 }}>
          {active ? `Filter · ${count}` : 'Filter'}
        </Typography>
        <ExpandMoreRoundedIcon sx={{ fontSize: '1rem' }} />
      </ButtonBase>

      <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <CheckboxItem label="Messages only" checked={messagesOnly} color={tone.fg} onClick={onToggleMessagesOnly} />
        {options.length > 0 && <Divider />}
        {options.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: '32px' }}>
            Add to feed
          </ListSubheader>
        )}
        {options.map((category) => (
          <CheckboxItem
            key={category}
            label={labelForCategory(category, tracks)}
            checked={added.has(category)}
            color={styleForCategory(category, tracks).fg}
            onClick={() => onToggle(category)}
          />
        ))}
      </Menu>
    </>
  )
}

export default FeedFilterMenu
